import React from 'react';

import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Dropdown from '../Dropdown/Dropdown';
import Gutter from '../Gutter/Gutter';
import Autocomplete, { SupportedInputType } from '../Autocomplete/Autocomplete';
import { OptionListOption } from '../OptionList/OptionList';
import { CreateProps } from '../../types/utils/CreateProps';

import styles from './SearchInputWithScope.module.scss';
import { BaseInputPropsWithoutChildren } from '../Input/BaseInput';
import BodyText from '../Typography/BodyText/BodyText';
import InputMessage from '../Input/InputMessage/InputMessage';

export type SearchInputWithScopeProps = CreateProps<
  {
    /** Custom className */
    className?: string;
    /** Show label space if label is empty */
    showLabel?: boolean;
    /** Label shown on the autocomplete */
    label?: string;
    /** Sets the input to disabled state */
    disabled?: boolean;
    /** The value that is set to the input */
    searchValue?: string;
    /** OnChange handler for the input */
    onSearchChange: (
      event: React.SyntheticEvent<HTMLDivElement>,
      value: string
    ) => void;
    /** List of options for search result */
    searchOptions?: OptionListOption[];
    /** Placeholder is visible when the input is empty */
    placeholder?: string;
    /** List of options for search scope aka. dropdown options */
    scopeOptions: OptionListOption[];
    /** Selected option of search scope */
    selectedScope: OptionListOption;
    /** OnChange handler for the search scope */
    onScopeChange: (
      event: React.SyntheticEvent<HTMLDivElement>,
      option: OptionListOption
    ) => void;
    /** Hadler when option is selected from search results */
    onSearchOptionSelect?: (
      event: React.SyntheticEvent<HTMLDivElement>,
      option: OptionListOption
    ) => void;
    /** Type of the input value */
    searchType?: SupportedInputType;
    /** OnClick handler for the icon */
    onIconClick?: () => void;
  },
  BaseInputPropsWithoutChildren,
  'onChange'
>;

export const SearchInputWithScope: React.FunctionComponent<SearchInputWithScopeProps> = ({
  className,
  showLabel = false,
  label,
  disabled,
  searchValue,
  onSearchChange,
  placeholder,
  scopeOptions,
  selectedScope,
  onScopeChange,
  searchOptions = [],
  onSearchOptionSelect,
  searchType,
  onIconClick,
  errorText,
  helperText,
  ...props
}) => {
  const showLabelLogic = showLabel || label;

  return (
    <Div className={[styles.searchInputWithScope, className]}>
      {showLabelLogic && (
        <Div>
          <BodyText
            block
            size="sm"
            color={disabled ? 'gray400' : 'black'}
            tagName="label"
            data-testid="label"
          >
            {label ? label : <>&nbsp;</>}
          </BodyText>
          <Gutter size="xs" />
        </Div>
      )}
      <Flex
        className={styles.searchInputWithScope_inputContainer}
        occupyVerticalSpace={false}
      >
        <Dropdown
          className={styles.searchInputWithScope_scopeDropdown}
          disabled={disabled}
          selectedOption={selectedScope}
          options={scopeOptions}
          onChange={onScopeChange}
          showCustomLabel={true}
          errorText={!!errorText ? ' ' : undefined}
        />
        <Autocomplete
          errorText={!!errorText ? ' ' : undefined}
          {...props}
          className={styles.searchInputWithScope_searchInput}
          value={searchValue}
          onChange={onSearchChange}
          placeholder={placeholder}
          disabled={disabled}
          options={searchOptions}
          onOptionSelect={onSearchOptionSelect}
          type={searchType}
          showCustomLabel={true}
          iconName={!!errorText ? 'error-filled' : 'search'}
          iconColor={disabled ? 'gray400' : !!errorText ? 'red600' : 'gray500'}
          onIconClick={onIconClick}
        />
      </Flex>
      {!!errorText && errorText !== ' ' && (
        <InputMessage text={errorText} color="red600" />
      )}
      {helperText ? <InputMessage text={helperText} color="gray500" /> : null}
    </Div>
  );
};

export default SearchInputWithScope;
